import { matchRoutes, useLocation } from 'react-router-dom'
import type { IRoutes } from '../interfaces/config'
import getUrlParams from '../utils/getUrlParams'
import useAppSelector from './useAppSelector'
import useSessionRouteConfigGuard from './useSessionRouteConfigGuard'

/**
 * The token a route declares when it needs a session, and the only thing this
 * guard derives its answer from. It is the param name every authored route
 * already uses — `/:slug/:sessionId`, `/:slug/:sessionId/cart` — so nothing
 * new has to be authored for an existing storefront to keep its behaviour.
 */
export const SESSION_ID_PARAM = 'sessionId'

/**
 * A discriminated union rather than `{ requiresSession: boolean; sessionId?:
 * string }`, and the reason is the **positive** narrowing, not the negative
 * one. `containers/Config.tsx` narrows on `requiresSession` and gets a
 * `string` inside that branch, so the guarded block needs no `!` assertion.
 * The flat `boolean` shape cannot do that — it rejects the *guarded* form too,
 * because a `boolean` does not narrow `sessionId`. Both shapes reject the
 * *unguarded* form equally; measured, by typechecking `Config.tsx` against
 * each.
 *
 * ⚠️ **Outside the required branch the field still exists**, declared
 * `sessionId?: undefined`. Keep it: it is what makes the destructure at
 * `Config.tsx`'s call site legal, and deleting it as a "simplification" is
 * `TS2339` there, not a tidy-up. (An earlier revision of this comment said the
 * field did not exist outside the branch, which describes a type that does not
 * compile against its own consumer — and someone acted on it.)
 *
 * **Name what that actually protects, because it is two of the three calls and
 * not the obvious one.** The teeth are the prefetch calls:
 * `fetchInitialCategoriesDataAction` and `fetchInitialCatalogDataAction` both
 * take `sessionId: string`, and unguarding the block raises two `TS2345`s, one
 * per call. `dispatch(setSessionId(sessionId))` beside them is **not**
 * protected — `setSessionId` is declared without a `PayloadAction<T>`
 * (`redux/slices/config/config.ts`), so its payload infers as `any` and
 * `setSessionId(undefined)` compiles happily. Measured, not assumed: moving
 * that one dispatch above the check leaves `tsc --noEmit` clean.
 *
 * **Nor does any test stop it, unless one asserts on a store that already
 * holds an id.** `initialState.sessionId` is `undefined` and the reducer
 * assigns the payload verbatim, so on a session-less route
 * `setSessionId(undefined)` leaves the store byte-identical to not dispatching
 * at all — every `toBeUndefined()` assertion is satisfied by the initial state
 * and the hoisted dispatch survives them. The kill case is
 * `ANTI-VACUITY: leaves a session id an earlier route stored alone`, which
 * seeds a real id first. Two earlier revisions of this comment named the type
 * and then a `toBeUndefined()` test as what held this line; neither did.
 *
 * If the two prefetch calls ever stop taking a bare `string`, the type stops
 * carrying even its two-of-three share.
 *
 * `sessionId` is typed `string` on the required branch to stay interchangeable
 * with `UrlParamsResult.sessionId`, which types the same value the same way.
 * Both are optimistic in the same place: a path too short to carry the segment
 * yields `undefined` at runtime under a `string` type. That is pre-existing
 * (see `getUrlParams`) and is deliberately not fixed here — `Config` passes the
 * value straight to the initial-data fetchers, and both of them already treat a
 * falsy session id as `ErrorMessageTranslation.InitialData`.
 */
export type SessionRequirement =
  | { requiresSession: true; sessionId: string }
  | { requiresSession: false; sessionId?: undefined }

/**
 * The route the shopper is on, matched against the authored table exactly as
 * `<Routes>` matches it — the ranking included, which is what
 * `matches[matches.length - 1]` picks out.
 *
 * Split out of `deriveSessionRequirement` for COM-973 rather than duplicated
 * into it: the misconfiguration report names the *authored route* the path
 * matched (`/:slug/:session` is what makes a renamed param legible), and a
 * report that re-ran the matching itself could describe a different match from
 * the one the decision was made on. `useSessionGuard` matches once and hands
 * both halves the same answer.
 *
 * **Deliberately unannotated, and the type below is derived from it rather
 * than the other way round.** `matchRoutes` is generic in its route-object
 * type and infers `{ path: string }` from the table this function builds, so
 * letting the inference through gives a match whose `route.path` is `string`.
 * Annotating the return type is what throws that away: an annotation has to
 * name a type, and the obvious one —
 * `NonNullable<ReturnType<typeof matchRoutes>>[number]` — instantiates the
 * generic at its **default**, i.e. the wide
 * `AgnosticRouteMatch<string, AgnosticRouteObject>`, whose `route.path` is
 * `string | undefined` and which carries members this table never has. That is
 * what was here before COM-973's review, under a docstring claiming it avoided
 * exactly that; measured with a type probe, not inferred. Widening it again
 * puts the `?.` back in `requirementFromMatch` and makes a `?? null` in the
 * report look load-bearing when it is not.
 *
 * @returns the winning match, or `undefined` where the table — which may not
 *   have arrived yet — answers for nothing.
 */
export const matchAuthoredRoute = (
  routes: IRoutes[] | undefined,
  pathname: string,
) => {
  const authored = routes?.map(({ route }) => ({ path: route })) ?? []
  const matches = matchRoutes(authored, pathname)
  return matches?.[matches.length - 1]
}

/**
 * One entry of what `matchAuthoredRoute` hands back — narrowed to this repo's
 * authored table, so `route.path` is a plain `string`. See that function's
 * docstring for why it is derived in this direction.
 */
export type AuthoredRouteMatch = NonNullable<
  ReturnType<typeof matchAuthoredRoute>
>

/**
 * The decision itself, over a match that has already been made. The token is
 * checked before the splat, so a splat route whose match carries a
 * `:sessionId` param reads its id from the match rather than from the second
 * segment.
 *
 * ⚠️ **There is exactly one `return { requiresSession: false }` in this
 * function, and `useSessionRouteConfigGuard` depends on that.** It reports on
 * the `false` arm alone and reaches the right silence on a session-bearing
 * route, a splat, a `NotFound` fall-through and the pre-config renders only
 * because all four resolve to `true` here. Adding a second producer of `false`
 * — or relaxing the fallback — starts a Datadog report firing from a diff that
 * says nothing about Datadog.
 */
export const requirementFromMatch = (
  match: AuthoredRouteMatch | undefined,
  positionalSessionId: string,
): SessionRequirement => {
  if (!match) {
    return { requiresSession: true, sessionId: positionalSessionId }
  }

  // A token in the params wins over everything below it, the splat
  // included: the match has already named the segment that holds the id, so
  // the positional split has nothing to add and is wrong wherever the route
  // puts the session anywhere but second.
  if (SESSION_ID_PARAM in match.params) {
    return {
      requiresSession: true,
      // Present by construction: `match.params` types every value optional,
      // and the check above is what establishes the key is there.
      sessionId: match.params[SESSION_ID_PARAM] as string,
    }
  }

  /*
   * A splat route whose match carries no `:sessionId` param is undecided, not
   * session-less, and takes the fallback too.
   *
   * `/:slug/*` names no `:sessionId` and would otherwise read as a route that
   * needs no session — but it matches *arbitrary* paths, including every
   * session-bearing one that no more specific route claimed, so the absence of
   * the token says nothing about the path in hand. Answering "no session" there
   * would take the session id and the prefetch away from a storefront that
   * authored a catch-all and no session-less route at all, which is exactly the
   * storefront this change has to leave alone. `defaultConfig` authors none, so
   * this is precaution rather than an observed case.
   *
   * This only ever sees a splat with no token in its params, because a token,
   * when present, has already won above. That includes an optional
   * `:sessionId?` that matched nothing: react-router leaves the key out
   * entirely, so such a route lands here and keeps the fallback. Keep this
   * check ahead of the `false` arm — it is what stops a splat from becoming a
   * second producer of `false`.
   */
  if (match.route.path.endsWith('*')) {
    return { requiresSession: true, sessionId: positionalSessionId }
  }

  return { requiresSession: false }
}

/**
 * Does the route the shopper is actually on need a session?
 *
 * The requirement is derived from the matched route's own config-authored
 * template: a route naming `:sessionId` requires one, a route that does not,
 * does not. There is no new config flag because the route declaration already
 * carries the information, and no code-side list of session-less paths because
 * the route table is the storefront's to author.
 *
 * `matchRoutes` is the function `<Routes>` matches that same table with, so
 * this asks the question react-router is about to answer rather than
 * pattern-matching the route string — the same reasoning, and the same
 * in-repo precedent, as `resolveExit` in `templates/OrderConfirmation.tsx`.
 * A string test would also accept a route the path does not match, and would
 * have to reimplement ranking where two authored routes both match.
 *
 * **A path the table cannot answer for keeps today's behaviour, deliberately.**
 * Three cases reach that: the boot renders before `fetchConfig` resolves, where
 * there is no route table to match against at all; a path that falls through to
 * `NotFound`; and a splat match whose params carry no `:sessionId`, which is
 * undecided rather than session-less (see below). An optional `:sessionId?`
 * that matched nothing counts as no token there. In all three, this returns
 * `requiresSession: true` with the positional session id `getUrlParams` reads
 * — i.e. exactly what `Config` did before this guard existed. A splat match
 * that does carry the token is not one of the three: it reads its id from the
 * match, as a concrete route does.
 *
 * The conservative default is the point. A storefront sees a change in two
 * places only: where one of its *own* authored, non-splat routes declares no
 * `:sessionId`, and the answer flips from the fallback to the derived one at
 * the same render the config arrives; and where one of its splat routes names
 * `:sessionId` anywhere but the second segment, which now reads the id from
 * the match instead of from that segment. This mirrors the denylist shape `routes/index.tsx` uses for
 * boot loaders (COM-858) — anything not named stays as it was.
 *
 * The pre-config case needs one thing from the caller, and reading this file
 * alone will get it wrong: `true` there is a *default*, not a derivation, so
 * `Config` must not raise `NonProvidedParams` on it. It waits for the config
 * before consulting this at all, because a session-less entry route boots with
 * no session id in the URL and nothing reachable from that error screen clears
 * `globalError` again — `manageErrors` is its only writer, and the two callers
 * that would reset it (`useGetCart`, `useGetSummary`) sit below `<Routes>`,
 * which `routes/index.tsx` does not render while the flag is set. The
 * reasoning is at that call site, which is where the conjunct lives.
 *
 * **The requirement comes from the token, not from the value.** A route
 * declaring `:sessionId` requires a session whether or not the segment
 * resolved to anything, so a short or malformed URL still reaches
 * `NonProvidedParams` as it does today.
 *
 * @param routes the SCS-authored route table, exactly as `routes/index.tsx`
 *   receives it. Typed as present on `IConfig` and nevertheless optional at
 *   runtime — that file guards `config.routes &&` before mapping it — so it is
 *   read defensively here too.
 * @param pathname the path to match, already relative to the router's
 *   `basename`. Authored routes are written without the ephemeral commit SHA
 *   or the language prefix, so this must be `useLocation().pathname` and not
 *   `window.location.pathname`.
 * @param positionalSessionId the pre-guard answer, used only where the table
 *   cannot answer.
 */
export const deriveSessionRequirement = (
  routes: IRoutes[] | undefined,
  pathname: string,
  positionalSessionId: string,
): SessionRequirement =>
  requirementFromMatch(
    matchAuthoredRoute(routes, pathname),
    positionalSessionId,
  )

/**
 * The per-route session guard `containers/Config.tsx` reads.
 *
 * `Config` renders above `<Routes>` and so has no route match of its own —
 * `useParams()` there is always `{}` — but it does render *inside*
 * `<BrowserRouter>`, so `useLocation()` is available and is what makes the
 * guard possible without moving the container below the route table.
 *
 * Reading the location subscribes `Config` to the router, so it now re-renders
 * on navigation where before it did not (its children are the same element
 * reference, so React bails out of the subtree). A navigation that stays on
 * one side of the session boundary re-runs nothing: `Config`'s effects key on
 * `config`, the session id, the slug and the requirement, and none of the four
 * move on `/acme/sess1` -> `/acme/sess1/cart`.
 *
 * **A navigation that crosses the boundary moves two of them, and that was a
 * defect (COM-974).** The requirement flips and the session id appears or
 * disappears, so the session effect — once-per-page-load by construction
 * before this hook existed — re-runs on every crossing. The leg back onto a
 * session-bearing route re-entered the guarded block and issued a second
 * catalog prefetch, whose `addLoader(LoadersType.INITIAL_DATA)` left
 * `routes/index.tsx`'s `<Routes>` holding nothing but its catch-all for the
 * length of the round trip. `Config` now keys the prefetch on the `(slug,
 * session id)` pair it last fetched; the reasoning is at that call site, and
 * both directions across the boundary are pinned in `sessionGuard.test.tsx`.
 *
 * ⚠️ Nor is a navigation the only thing that re-runs it. `config` is one of
 * those four deps, and `setSessionData` recomputes
 * `config.options.hidePricesActive`, which moves the object's identity under
 * Immer — so on a `hidePrices` storefront the effect re-runs with no
 * navigation at all. Also COM-974; see the call site.
 *
 * **It reports as well as answering, as of COM-973.** A route that declares no
 * `:sessionId` is the one answer this guard cannot tell apart from an authoring
 * slip, so `useSessionRouteConfigGuard` turns it into a Datadog RUM error. It
 * is mounted here rather than in `containers/Config.tsx` because the report
 * needs the pathname, the storefront slug, the positional session id and the
 * matched route — four values this hook computes and none of which
 * `SessionRequirement` carries. See that hook's docstring, and the note on the
 * design in `requirementFromMatch` above.
 */
export const useSessionGuard = (): SessionRequirement => {
  const routes = useAppSelector((state) => state.defaultSlice.config?.routes)
  const { pathname } = useLocation()
  // `storeName` is taken from the same call rather than from
  // `generateDataDogContext`'s own first-segment split, which reads the commit
  // SHA on an ephemeral deployment and the language on a prefixed one. See the
  // `slug` arg's docstring.
  const { storeName: slug, sessionId: positionalSessionId } = getUrlParams(
    window.location.pathname,
  )

  // Matched once, read twice: the requirement and the report it may produce
  // have to describe the same match. See `matchAuthoredRoute`.
  const match = matchAuthoredRoute(routes, pathname)
  const requirement = requirementFromMatch(match, positionalSessionId)

  useSessionRouteConfigGuard({
    requiresSession: requirement.requiresSession,
    pathname,
    slug,
    positionalSessionId,
    matchedRoutePath: match?.route.path,
  })

  return requirement
}

export default useSessionGuard
